import { AddableCourse, CourseLite, CourseOption, CourseSummary } from "../../types/course.response";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import { DEFAULT_SEMESTER } from "../../constants";
import { getSemesterName } from "../../utils/semesterUtils";
import { useHydratedCourses } from "../../hooks/useHydratedCourses";
import CourseRow from "../course-row/CourseRow";
import ChevronIcon from "../icons/ChevronIcon";

interface SectionCourseListProps {
    courses: CourseSummary[];
    requirementId: string;
    requirementName: string;
    visibleLimit: number;
    initialLimit: number;
    handleAddCourse: (newCourse: AddableCourse, overrides: CourseRequirementOverride[], semester: string | null, classSectionIds?: number[]) => void;
    handleDeleteCourse: (deletedCourse: CourseLite) => void;
    handleFavoriteCourse: (courseOption: CourseLite) => void;
    optimisticFavorites: Map<string, boolean>;
    userCourses?: CourseOption[];
    onShowMore: () => void;
    onSeeAll: () => void;
    /** GROUP 槽位:mapKey -> 组内可选课数,传给 CourseRow 展示 "👁 N options" */
    optionCountByKey?: Map<string, number>;
    /** "Show more" 按钮文案,GROUP 传 "Show more groups";默认 "Show more" */
    showMoreLabel?: string;
    /** GROUP 槽位:点击某行尾部眼睛,按 mapKey 打开本组全部课程弹窗 */
    onShowOptions?: (mapKey: string) => void;
    /** 空列表文案;broad(内涵)节点池 = 用户命中课,空态语义与普通池不同 */
    emptyMessage?: string;
}

export default function SectionCourseList({
    courses,
    requirementId,
    requirementName,
    visibleLimit,
    initialLimit,
    handleAddCourse,
    handleDeleteCourse,
    handleFavoriteCourse,
    optimisticFavorites,
    userCourses,
    onShowMore,
    onSeeAll,
    optionCountByKey,
    showMoreLabel = "Show more",
    onShowOptions,
    emptyMessage,
}: SectionCourseListProps) {
    // 补水边界:只为可见切片拉取完整课程数据,"show more" 扩大切片即自动补水
    const visibleCourses = courses.slice(0, visibleLimit);
    const hydrated = useHydratedCourses(visibleCourses);

    if (courses.length === 0) {
        return (
            <tr>
                <td colSpan={4} className="text-sm text-gray-400 italic">
                    {emptyMessage ?? `No available courses in ${getSemesterName(DEFAULT_SEMESTER)}`}
                </td>
            </tr>
        );
    }

    return (
        <>
            {visibleCourses.map(summary => (
                <CourseRow
                    key={summary.mapKey}
                    course={summary}
                    hydrated={hydrated.get(summary.mapKey)}
                    selectedSemester={DEFAULT_SEMESTER}
                    onAddToSchedule={(course, classSectionIds) => handleAddCourse(course, [], DEFAULT_SEMESTER, classSectionIds)}
                    onMarkAsTaken={(course) => handleAddCourse(course, [], null)}
                    onDeleteFromSchedule={(course) => handleDeleteCourse(course)}
                    onFavoriteToggle={(course) => handleFavoriteCourse(course)}
                    optimisticFavorites={optimisticFavorites}
                    userCourses={userCourses}
                    requirementId={requirementId}
                    groupOptionCount={optionCountByKey?.get(summary.mapKey)}
                    onShowOptions={onShowOptions ? () => onShowOptions(summary.mapKey) : undefined}
                />
            ))}
            {(courses.length > visibleLimit || visibleLimit > initialLimit) && (
                <tr>
                    <td colSpan={4} className="py-2 border-b border-gray-50">
                        {visibleLimit === initialLimit ? (
                            <button
                                onClick={onShowMore}
                                className="flex items-center justify-center w-full gap-1 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                <ChevronIcon direction="down" className="w-4 h-4" />
                                <span>{showMoreLabel}</span>
                            </button>
                        ) : (
                            <button
                                onClick={onSeeAll}
                                className="flex items-center justify-center w-full gap-1 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                            >
                                <span>See all courses in {requirementName} requirement</span>
                                <ChevronIcon direction="right" className="w-4 h-4" />
                            </button>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
}
